import React, { useEffect, useState, useRef } from 'react';
import { useTranslation } from '../i18n/i18n';
import { useTheme, useMediaQuery } from '@mui/material';
import { Box, Typography, Button, Modal, TextField, Grid, Card, CardContent, FormControl, FormControlLabel, InputLabel, Select, MenuItem, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Checkbox, IconButton, Menu, ListItemIcon, ListItemText, Chip, List, ListItemButton, ListItem, Divider, Avatar } from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress'; // Import spinner
import MoreVertIcon from '@mui/icons-material/MoreVert';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import FilterListIcon from '@mui/icons-material/FilterList';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import API_ENDPOINTS from '../config/apiConfig';
import { useBranch } from '../context/BranchContext';
import './Hirees.css'; // shared styles for card sizing and clamping
import AdminUserDetailsModal from './AdminUserDetailsModal'; // Add this import
import FixedSnackbar from './FixedSnackbar';
import LoadingButton from './LoadingButton';
import MultiSelectDialog from './MultiSelectDialog';
import MultiSelectPopover from './MultiSelectPopover';
import CandidateDetailsPanel from './CandidateDetailsPanel';

function Candidates() {
  const { t } = useTranslation();
  const initialNewCandidate = {
    email: '',
    fullname: '',
    position_id: '',
  };
  const [candidates, setCandidates] = useState([]);
  const [filteredCandidates, setFilteredCandidates] = useState([]); // For search functionality
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [learningModules, setLearningModules] = useState([]);
  const [selectedModules, setSelectedModules] = useState([]);
  const [pendingSelectedModules, setPendingSelectedModules] = useState([]);
  const [openModal, setOpenModal] = useState(false);
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
  const [positions, setPositions] = useState([]); // State to store available positions
  const [newCandidate, setNewCandidate] = useState(initialNewCandidate);
  // Reset form and close modal
  const handleCloseModal = () => {
    setOpenModal(false);
    setNewCandidate({ ...initialNewCandidate });
    setNewCandidateVacancies([]);
  };
  const [openModuleSelectionDialog, setOpenModuleSelectionDialog] = useState(false); // New state for module selection dialog
  const [moduleSearchQuery, setModuleSearchQuery] = useState('');
  const [loading, setLoading] = useState(false); // State to track loading
  const [loadingMessage, setLoadingMessage] = useState(''); // State for the loading message
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' }); // Snackbar state
  const [openRequestDocsDialog, setOpenRequestDocsDialog] = useState(false); // New state for request documents dialog
  const [loadingRequestDocs, setLoadingRequestDocs] = useState(false); // Add for request docs
  const [loadingUpgrade, setLoadingUpgrade] = useState(false); // Add for upgrade
  const [detailsModalOpen, setDetailsModalOpen] = useState(false); // Add state for modal
  const [fetching, setFetching] = useState(true);
  const [userRole, setUserRole] = useState(null);
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [menuCandidate, setMenuCandidate] = useState(null);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [deleteReason, setDeleteReason] = useState('');
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [editForm, setEditForm] = useState({ fullname: '', position_id: '', branches: [] });
  const [loadingEdit, setLoadingEdit] = useState(false);
  const [isSupervisor, setIsSupervisor] = useState(false);
  const [supervisors, setSupervisors] = useState([]);
  const [selectedSupervisorId, setSelectedSupervisorId] = useState('');
  const [openSupervisorDialog, setOpenSupervisorDialog] = useState(false);
  const [selectedBranchesForUpgrade, setSelectedBranchesForUpgrade] = useState([]);
  const [openBranchesDialog, setOpenBranchesDialog] = useState(false);
  const { branches: availableBranches } = useBranch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm')); // true for xs and sm
  const detailsRef = useRef(null);
  const [allVacancies, setAllVacancies] = useState([]);
  const [openEditVacanciesDialog, setOpenEditVacanciesDialog] = useState(false);
  const [editVacanciesSelected, setEditVacanciesSelected] = useState([]);
  const [savingVacancies, setSavingVacancies] = useState(false);
  const [newCandidateVacancies, setNewCandidateVacancies] = useState([]);

  // Auto-scroll to details panel when candidate is selected
  useEffect(() => {
    // fetch current user role to show/hide admin actions
    const fetchUser = async () => {
      try {
        const res = await fetch(`${API_ENDPOINTS.BASEURL}/api/user/me`, { credentials: 'include' });
        if (res.ok) {
          const data = await res.json();
          setUserRole(data.role);
        }
      } catch (err) {
        // ignore
      }
    };
    fetchUser();
    if (selectedCandidate && detailsRef.current) {
      setTimeout(() => {
        detailsRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 50);
    }
  }, [selectedCandidate]);

  useEffect(() => {
    const fetchCandidates = async () => {
      try {
        setFetching(true);
        const response = await fetch(`${API_ENDPOINTS.BASEURL}/api/candidates`, { credentials: 'include' });
        const data = await response.json();
        // API now returns only candidates, no client-side filtering required
        const candidatesList = data || [];
        setCandidates(candidatesList);
        setFilteredCandidates(candidatesList);
      } catch (err) {
        setCandidates([]);
        setFilteredCandidates([]);
      } finally {
        setFetching(false);
      }
    };

    const fetchLearningModules = async () => {
      try {
        const response = await fetch(API_ENDPOINTS.LEARNING_MODULES_ALL_META, { credentials: 'include' });
        const data = await response.json();
        setLearningModules(data);
      } catch (err) {
        setLearningModules([]);
      }
    };

    const fetchSupervisors = async () => {
      try {
        const res = await fetch(`${API_ENDPOINTS.BASEURL}/api/supervisors`, { credentials: 'include' });
        if (res.ok) {
          const data = await res.json();
          setSupervisors(data || []);
        }
      } catch (err) {
        setSupervisors([]);
      }
    };

    fetchCandidates();
    fetchLearningModules();
    fetchSupervisors();
  }, []);

  const [searchQuery, setSearchQuery] = useState(''); // For search input

  const handleSearch = (event) => {
    const query = event.target.value.toLowerCase();
    setSearchQuery(query);
    setFilteredCandidates(
      candidates.filter(
        (candidate) =>
          (candidate.fullname || '').toLowerCase().includes(query) ||
          (candidate.email || '').toLowerCase().includes(query) ||
          (candidate.position || '').toLowerCase().includes(query)
      )
    );
  };

  // Fetch available positions from the API
  useEffect(() => {
    const fetchPositions = async () => {
      try {
        const response = await fetch(`${API_ENDPOINTS.BASEURL}/api/positions`, { credentials: 'include' });
        if (response.ok) {
          const data = await response.json();
          console.log('Positions fetched:', data); // Debug log to verify the data
          setPositions(data); // Populate positions state
        } else {
          console.error('Failed to fetch positions');
        }
      } catch (error) {
        console.error('Error fetching positions:', error);
      }
    };

    fetchPositions();

    const fetchVacancies = async () => {
      try {
        const res = await fetch(API_ENDPOINTS.VACANCIES, { credentials: 'include' });
        if (res.ok) {
          const data = await res.json();
          setAllVacancies(Array.isArray(data) ? data : []);
        }
      } catch { /* silent */ }
    };
    fetchVacancies();
  }, []);

  // selecting a candidate is handled inline where needed

  const handleConfirmUpgrade = () => {
    const defaultModuleIds = learningModules
      .filter(m => m.is_default)
      .map(m => m.videoid);
    setSelectedModules(defaultModuleIds);
    setPendingSelectedModules(defaultModuleIds);
    // Always open supervisor selection next so a supervisor can also be assigned a supervisor
    setOpenConfirmDialog(false);
    setOpenBranchesDialog(true);
  };

  const handleUpgradeCandidate = async (moduleIds = selectedModules) => {
    if (!selectedCandidate || moduleIds.length === 0) return;
    if (!selectedBranchesForUpgrade || selectedBranchesForUpgrade.length === 0) {
      setSnackbar({ open: true, message: t('please_select_at_least_one_branch') || 'Please select at least one branch.', severity: 'error' });
      return;
    }
    setLoadingUpgrade(true); // Start spinner
    try {
      const response = await fetch(`${API_ENDPOINTS.BASEURL}/api/candidates/${selectedCandidate.email}/upgrade`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ learning_modules: moduleIds, is_supervisor: isSupervisor, supervisor_id: selectedSupervisorId || null, branches: selectedBranchesForUpgrade }),
      });

      if (response.ok) {
        setSnackbar({ open: true, message: t('upgrade_candidate_success'), severity: 'success' });
        setCandidates((prev) =>
          prev.filter((candidate) => candidate.email !== selectedCandidate.email)
        );
        setFilteredCandidates((prev) => prev.filter((candidate) => candidate.email !== selectedCandidate.email));
        setSelectedCandidate(null);
      } else {
        const errorData = await response.json();
        const serverMsg = errorData?.message_key ? t(errorData.message_key) : (errorData?.message ? `${t('upgrade_candidate_failure')}: ${errorData.message}` : t('upgrade_candidate_failure'));
        setSnackbar({ open: true, message: serverMsg, severity: 'error' });
      }
    } catch (error) {
      console.error('Error upgrading candidate:', error);
      setSnackbar({ open: true, message: t('upgrade_candidate_error'), severity: 'error' });
    } finally {
      setLoadingUpgrade(false); // Stop spinner
      setOpenModuleSelectionDialog(false);
      setOpenConfirmDialog(false);
      setIsSupervisor(false);
        setSelectedSupervisorId('');
    }
  };

  const handleCancelModuleSelection = () => {
    setPendingSelectedModules([...selectedModules]);
    setOpenModuleSelectionDialog(false);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewCandidate((prev) => ({ ...prev, [name]: value }));
  };

  const handleAddCandidate = async () => {
    try {
      setLoading(true);
      setLoadingMessage(t('sending_invitation'));

      const candidateToSubmit = { ...newCandidate, vacancy_ids: newCandidateVacancies };
      const response = await fetch(`${API_ENDPOINTS.BASEURL}/api/candidate/add`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(candidateToSubmit),
      });

      if (response.ok) {
        setSnackbar({ open: true, message: t('add_candidate_success'), severity: 'success' });
        setOpenModal(false);
        setNewCandidate({
          email: '',
          fullname: '',
          position_id: '',
        });
        setNewCandidateVacancies([]);
        const updatedCandidates = await fetch(`${API_ENDPOINTS.BASEURL}/api/candidates`, { credentials: 'include' });
        const data = await updatedCandidates.json();
        // API returns filtered candidate list; no client-side filtering needed
        const candidatesList = data;
        setCandidates(candidatesList);
        setFilteredCandidates(candidatesList);
      } else {
        const errorData = await response.json();
        setOpenModal(false); // Close modal on error
        // Prefer server-provided translation key, fallback to server message, then generic message
        const serverMsg = errorData?.message_key ? t(errorData.message_key) : (errorData?.message || t('add_candidate_failure'));
        setSnackbar({ open: true, message: serverMsg, severity: 'error' });
      }
    } catch (error) {
      console.error(t('add_candidate_failure'), error);
      setOpenModal(false); // Close modal on error
      setSnackbar({ open: true, message: t('add_candidate_failure'), severity: 'error' });
    } finally {
      setLoading(false);
      setLoadingMessage('');
    }
  };

  const handleSnackbarClose = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // Called when AdminUserDetailsModal deletes a user
  const handleUserDeleted = (email) => {
    setCandidates((prev) => prev.filter((c) => c.email !== email));
    setFilteredCandidates((prev) => prev.filter((c) => c.email !== email));
    setSelectedCandidate(null);
    setDetailsModalOpen(false);
    setSnackbar({ open: true, message: t('delete_success'), severity: 'success' });
  };

  // Helper to determine where to show the info bubble
  const getBubbleIndex = () => {
    if (!selectedCandidate) return -1;
    return filteredCandidates.findIndex((c) => c.email === selectedCandidate.email);
  };

  // Helper to unselect candidate (collapse bubble)
  const handleUnselectCandidate = () => setSelectedCandidate(null);

  // Calculate how many cards per row (for desktop, adjust as needed)
  // One card per row on mobile so the details panel always follows the selected card.
  const cardsPerRow = isMobile ? 1 : 3;

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', px: { xs: 2, sm: 3, md: 4 }, py: { xs: 3, sm: 4 } }}>
      {/* Page Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: { xs: 'flex-start', sm: 'center' }, flexDirection: { xs: 'column', sm: 'row' }, gap: 2, mb: { xs: 3, sm: 4 } }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
            {t('candidates_title') || 'Candidates'}
          </Typography>
          <Typography variant="body1" sx={{ color: 'text.secondary', maxWidth: 640 }}>
            {t('candidates_subtitle') || 'Manage candidate applications, review prospects, and process hires.'}
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<PersonAddIcon />} onClick={() => setOpenModal(true)} sx={{ textTransform: 'none', fontWeight: 600, boxShadow: 'none', '&:hover': { boxShadow: '0 2px 8px rgba(0,0,0,0.12)' } }}>
          {t('add_new_candidate')}
        </Button>
      </Box>

      {/* Search Bar */}
      <TextField
        size="small"
        variant="outlined"
        placeholder={t('search') || 'Search candidates...'}
        value={searchQuery}
        onChange={handleSearch}
        InputProps={{
          startAdornment: <FilterListIcon fontSize="small" sx={{ color: '#62B6CB', mr: 0.5 }} />
        }}
        sx={{ minWidth: { xs: '100%', sm: 200 }, maxWidth: 350, mb: 3 }}
      />

      <Grid container spacing={2}>
        {fetching ? (
          <Grid item xs={12}>
            <Box sx={{ textAlign: 'center', py: 8 }}>
              <CircularProgress />
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                Loading candidates...
              </Typography>
            </Box>
          </Grid>
        ) : filteredCandidates.length === 0 ? (
          <Grid item xs={12}>
            <Box sx={{ textAlign: 'center', py: 8, px: 3 }}>
              <Avatar sx={{ width: 64, height: 64, mb: 2, bgcolor: 'grey.100', color: 'grey.400', mx: 'auto' }}>
                <PersonAddIcon sx={{ fontSize: 32 }} />
              </Avatar>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                {t('no_candidates_found')}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                No candidates match your current filters.
              </Typography>
            </Box>
          </Grid>
        ) : (
          filteredCandidates.map((candidate, idx) => {
          const isSelected = selectedCandidate?.email === candidate.email;
          // Determine if this is the last card in the row
          const isLastInRow = (idx + 1) % cardsPerRow === 0;
          // Determine if the info bubble should be rendered after this row
          const bubbleIndex = getBubbleIndex();
          const isBubbleRow =
            bubbleIndex !== -1 &&
            (Math.floor(bubbleIndex / cardsPerRow) === Math.floor(idx / cardsPerRow)) &&
            (isLastInRow || idx === filteredCandidates.length - 1);

          return (
            <React.Fragment key={candidate.email}>
              <Grid item xs={12} sm={4} md={4} sx={{ position: 'relative' }}>
                <div className={isSelected ? 'selected-card-wrapper' : ''}>
                  <Card
                    className="user-card"
                    onClick={() =>
                      isSelected ? handleUnselectCandidate() : setSelectedCandidate(candidate)
                    }
                    sx={{
                      cursor: 'pointer',
                      borderRadius: 2,
                      border: 1,
                      borderColor: isSelected ? 'primary.main' : 'divider',
                      boxShadow: isSelected ? '0 0 0 2px' + theme.palette.primary.main + '40' : 'none',
                      transition: 'all 0.15s',
                      '&:hover': { borderColor: 'primary.light', boxShadow: 1 },
                      position: 'relative',
                      zIndex: 3,
                      minHeight: 180,
                      display: 'flex',
                      flexDirection: 'column'
                    }}
                  >
                    <CardContent sx={{ p: { xs: 2, sm: 2.5 }, flex: 1, display: 'flex', flexDirection: 'column' }}>
                      <Typography variant="h6" className="card-title" title={candidate.fullname} noWrap sx={{ width: '100%', fontWeight: 600 }}>{candidate.fullname}</Typography>
                      <Typography variant="body2" color="text.secondary">{t('email')}: {candidate.email}</Typography>
                      <Typography variant="body2" color="text.secondary">{t('position')}: {candidate.position}</Typography>
                      <Chip
                        label={candidate.status}
                        size="small"
                        color={candidate.status === 'active' ? 'success' : candidate.status === 'pending' ? 'warning' : 'default'}
                        sx={{ mt: 0.5, fontWeight: 500 }}
                      />
                      {candidate.vacancy_ids?.length > 0 && (
                        <Box sx={{ mt: 'auto', pt: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Chip label={candidate.vacancy_ids[0].title} size="small" color="primary" variant="outlined" sx={{ height: 24, fontSize: '0.75rem' }} />
                          {candidate.vacancy_ids.length > 1 && (
                            <Chip label={`+${candidate.vacancy_ids.length - 1}`} size="small" color="default" variant="outlined" sx={{ height: 24, fontSize: '0.75rem', minWidth: 32 }} />
                          )}
                        </Box>
                      )}
                      {['admin', 'superadmin'].includes(userRole) && (
                        <IconButton
                          size="small"
                          aria-controls={menuAnchorEl ? 'candidate-menu' : undefined}
                          aria-haspopup="true"
                          onClick={(e) => { e.stopPropagation(); setMenuAnchorEl(e.currentTarget); setMenuCandidate(candidate); }}
                          sx={{ position: 'absolute', top: 8, right: 8 }}
                        >
                          <MoreVertIcon />
                        </IconButton>
                      )}
                    </CardContent>
                  </Card>
                  {/* Vertical lines for selected card */}
                  {isSelected && (
                    <>
                      <div className="vertical-line left" />
                      <div className="vertical-line right" />
                    </>
                  )}
                </div>
              </Grid>
              {isBubbleRow && bubbleIndex !== -1 && (
                <Grid item xs={12} sx={{ position: 'relative' }} ref={detailsRef}>
                  <CandidateDetailsPanel
                    candidate={selectedCandidate}
                    isAdmin={['admin', 'superadmin'].includes(userRole)}
                    onClose={handleUnselectCandidate}
                    onOpenMenu={(e) => { e.stopPropagation(); setMenuAnchorEl(e.currentTarget); setMenuCandidate(selectedCandidate); }}
                    onEditVacancies={() => {
                      setEditVacanciesSelected((selectedCandidate.vacancy_ids || []).map(v => v._id));
                      setOpenEditVacanciesDialog(true);
                    }}
                    onRequestDocs={() => setOpenRequestDocsDialog(true)}
                    onHire={() => { setSelectedBranchesForUpgrade([]); setOpenConfirmDialog(true); }}
                    onViewFull={() => setDetailsModalOpen(true)}
                  />
                  {!isMobile && <div className="horizontal-connector" />}
                </Grid>
              )}
            </React.Fragment>
          );
        }))}
      </Grid>

      <Menu
        id="candidate-menu"
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={() => { setMenuAnchorEl(null); setMenuCandidate(null); }}
      >
        <MenuItem
          onClick={() => {
            setEditForm({
              fullname: menuCandidate?.fullname || '',
              position_id: menuCandidate?.position_id || positions.find(position => position.display_text === menuCandidate?.position)?.id || '',
              branches: menuCandidate?.branches || [],
            });
            setOpenEditDialog(true);
            setMenuAnchorEl(null);
          }}
        >
          <ListItemIcon>
            <EditIcon fontSize="small" color="primary" />
          </ListItemIcon>
          <ListItemText primary={t('edit')} />
        </MenuItem>
        <MenuItem
          onClick={() => {
            setOpenDeleteDialog(true);
            setMenuAnchorEl(null);
          }}
        >
          <ListItemIcon>
            <DeleteIcon fontSize="small" color="error" />
          </ListItemIcon>
          <ListItemText primary={t('delete')} />
        </MenuItem>
      </Menu>

      <Dialog
        open={openDeleteDialog}
        onClose={() => { setOpenDeleteDialog(false); setDeleteReason(''); }}
      >
        <DialogTitle>{t('confirm_delete')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('confirm_delete_prompt')} {menuCandidate?.fullname}?
          </DialogContentText>
          <TextField
            autoFocus
            margin="dense"
            label={t('reason_optional')}
            type="text"
            fullWidth
            value={deleteReason}
            onChange={(e) => setDeleteReason(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setOpenDeleteDialog(false); setDeleteReason(''); }}>
            {t('cancel')}
          </Button>
          <Button
            onClick={async () => {
              if (!menuCandidate) return;
              try {
                const res = await fetch(`${API_ENDPOINTS.BASEURL}/api/users/${encodeURIComponent(menuCandidate.email)}/delete`, {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  credentials: 'include',
                  body: JSON.stringify({ confirm: true, reason: deleteReason })
                });
                if (res.ok) {
                  setSnackbar({ open: true, message: t('delete_success'), severity: 'success' });
                  setCandidates((prev) => prev.filter((c) => c.email !== menuCandidate.email));
                  setFilteredCandidates((prev) => prev.filter((c) => c.email !== menuCandidate.email));
                  setSelectedCandidate(null);
                } else {
                  setSnackbar({ open: true, message: t('delete_failed'), severity: 'error' });
                }
              } catch (err) {
                setSnackbar({ open: true, message: t('delete_failed'), severity: 'error' });
              } finally {
                setOpenDeleteDialog(false);
                setDeleteReason('');
                setMenuCandidate(null);
              }
            }}
            color="error"
            variant="contained"
          >
            {t('delete')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={openEditDialog} onClose={() => setOpenEditDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('edit_user')}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label={t('name')}
            fullWidth
            value={editForm.fullname}
            onChange={(e) => setEditForm({ ...editForm, fullname: e.target.value })}
          />
          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel>{t('position')}</InputLabel>
            <Select
              value={editForm.position_id}
              onChange={(e) => setEditForm({ ...editForm, position_id: e.target.value })}
              label={t('position')}
            >
              {positions.map((position) => (
                <MenuItem key={position.id} value={position.id}>
                  {position.display_text}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <MultiSelectPopover
            label={t('branches')}
            placeholder={t('select_branches')}
            selectedValues={editForm.branches}
            options={availableBranches.map((branch) => ({
              id: branch.branch_id || branch.code || branch._id,
              label: branch.name,
              description: branch.branch_id || branch.code,
            }))}
            onApply={(branches) => setEditForm((current) => ({ ...current, branches }))}
            searchLabel={t('search')}
            emptyLabel={t('no_branches_found')}
            applyLabel={t('apply')}
            cancelLabel={t('cancel')}
            clearLabel={t('clear')}
            closeLabel={t('close')}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenEditDialog(false)}>{t('cancel')}</Button>
          <LoadingButton
            onClick={async () => {
              if (!editForm.fullname.trim()) {
                setSnackbar({ open: true, message: t('name_required'), severity: 'error' });
                return;
              }
              if (!editForm.position_id) {
                setSnackbar({ open: true, message: t('position_required'), severity: 'error' });
                return;
              }
              if (editForm.branches.length === 0) {
                setSnackbar({ open: true, message: t('please_select_at_least_one_branch'), severity: 'error' });
                return;
              }
              const positionLabel = positions.find(position => position.id === editForm.position_id)?.display_text || '';
              setLoadingEdit(true);
              try {
                const res = await fetch(API_ENDPOINTS.UPDATE_USER(menuCandidate.email), {
                  method: 'PUT',
                  headers: { 'Content-Type': 'application/json' },
                  credentials: 'include',
                  body: JSON.stringify(editForm),
                });
                if (res.ok) {
                  setSnackbar({ open: true, message: t('user_updated'), severity: 'success' });
                  setCandidates((prev) =>
                    prev.map((c) =>
                      c.email === menuCandidate.email
                        ? { ...c, fullname: editForm.fullname, position_id: editForm.position_id, position: positionLabel, branches: editForm.branches }
                        : c
                    )
                  );
                  setFilteredCandidates((prev) =>
                    prev.map((c) =>
                      c.email === menuCandidate.email
                        ? { ...c, fullname: editForm.fullname, position_id: editForm.position_id, position: positionLabel, branches: editForm.branches }
                        : c
                    )
                  );
                  if (selectedCandidate?.email === menuCandidate.email) {
                    setSelectedCandidate((prev) => ({
                      ...prev,
                      fullname: editForm.fullname,
                      position_id: editForm.position_id,
                      position: positionLabel,
                      branches: editForm.branches,
                    }));
                  }
                  setOpenEditDialog(false);
                } else {
                  const err = await res.json();
                  setSnackbar({ open: true, message: err.message || t('update_failed'), severity: 'error' });
                }
              } catch (err) {
                setSnackbar({ open: true, message: t('update_failed'), severity: 'error' });
              } finally {
                setLoadingEdit(false);
              }
            }}
            color="primary"
            variant="contained"
            loading={loadingEdit}
            loadingLabel={t('please_wait')}
          >
            {t('save')}
          </LoadingButton>
        </DialogActions>
      </Dialog>

      <MultiSelectDialog
        open={openBranchesDialog}
        onClose={() => { setOpenBranchesDialog(false); setOpenConfirmDialog(true); }}
        options={availableBranches.map((branch) => ({
          id: branch.branch_id || branch.code || branch._id,
          label: branch.name,
          description: branch.branch_id || branch.code,
        }))}
        selectedValues={selectedBranchesForUpgrade}
        onApply={(branches) => {
          if (!branches.length) {
            setSnackbar({
              open: true,
              message: t('please_select_at_least_one_branch') || 'Please select at least one branch.',
              severity: 'error',
            });
            return;
          }
          setSelectedBranchesForUpgrade(branches);
          setOpenBranchesDialog(false);
          setOpenSupervisorDialog(true);
        }}
        title={t('select_branches') || 'Select Branches'}
        description={t('select_branches_prompt') || `Select branch(es) for ${selectedCandidate?.fullname || ''}.`}
        searchLabel={t('search') || 'Search'}
        emptyLabel={t('no_branches_found') || 'No branches found'}
        confirmLabel={t('apply') || 'Apply'}
        cancelLabel={t('cancel') || 'Cancel'}
        clearLabel={t('clear') || 'Clear'}
        closeLabel={t('close') || 'Close'}
        showClear
        required
        validationMessage={t('please_select_at_least_one_branch') || 'Please select at least one branch.'}
      />

      <Dialog
        open={openModuleSelectionDialog}
        onClose={handleCancelModuleSelection}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{t('confirm_module_selection')}</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            {t('select_learning_modules')} {selectedCandidate?.fullname}.
          </DialogContentText>
          <TextField
            fullWidth
            size="small"
            placeholder={t('search_modules')}
            value={moduleSearchQuery}
            onChange={(e) => setModuleSearchQuery(e.target.value)}
            sx={{ mb: 1 }}
          />
          <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
            <Button size="small" onClick={() => {
              const filtered = learningModules
                .filter(m => !moduleSearchQuery || (m.title || '').toLowerCase().includes(moduleSearchQuery.toLowerCase()))
                .map(m => m.videoid);
              setPendingSelectedModules((current) => [...new Set([...current, ...filtered])]);
            }}>
              {t('select_all')}
            </Button>
            <Button size="small" onClick={() => {
              const filteredIds = learningModules
                .filter(m => !moduleSearchQuery || (m.title || '').toLowerCase().includes(moduleSearchQuery.toLowerCase()))
                .map(m => m.videoid);
              setPendingSelectedModules((current) => current.filter(id => !filteredIds.includes(id)));
            }}>
              {t('deselect_all')}
            </Button>
          </Box>
          <List sx={{ maxHeight: 400, overflow: 'auto', border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
            {(() => {
              const filtered = learningModules
                .filter(m => !moduleSearchQuery || (m.title || '').toLowerCase().includes(moduleSearchQuery.toLowerCase()))
                .sort((a, b) => {
                  if (!!a.is_default !== !!b.is_default) return a.is_default ? -1 : 1;
                  return (a.title || '').localeCompare(b.title || '');
                });
              if (filtered.length === 0) {
                return (
                  <ListItem>
                    <ListItemText primary={t('no_modules_available') || 'No modules found'} />
                  </ListItem>
                );
              }
              let lastWasDefault = null;
              return filtered.map((module, idx) => {
                const showDivider = idx > 0 && module.is_default !== filtered[idx - 1].is_default;
                return (
                  <React.Fragment key={module.videoid}>
                    {showDivider && <Divider />}
                    <ListItemButton onClick={() => {
                      setPendingSelectedModules(prev =>
                        prev.includes(module.videoid)
                          ? prev.filter(id => id !== module.videoid)
                          : [...prev, module.videoid]
                      );
                    }}>
                      <Checkbox checked={pendingSelectedModules.includes(module.videoid)} sx={{ mr: 1 }} />
                      <ListItemText
                        primary={module.title}
                        secondary={module.source === 'custom' ? t('clm_source_custom') : t('clm_source_shared')}
                      />
                      {module.is_default && (
                        <Chip label={t('is_default')} size="small" color="primary" sx={{ ml: 1 }} />
                      )}
                    </ListItemButton>
                  </React.Fragment>
                );
              });
            })()}
          </List>
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            {t('modules_selected_count').replace('{count}', pendingSelectedModules.length)}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelModuleSelection} color="primary" disabled={loadingUpgrade}>
            {t('cancel')}
          </Button>
          <LoadingButton
            onClick={() => {
              const moduleIds = [...pendingSelectedModules];
              setSelectedModules(moduleIds);
              handleUpgradeCandidate(moduleIds);
            }}
            color="secondary"
            variant="contained"
            disabled={pendingSelectedModules.length === 0}
            loading={loadingUpgrade}
            loadingLabel={t('please_wait')}
          >
            {t('confirm')}
          </LoadingButton>
        </DialogActions>
      </Dialog>

      <Dialog
        open={openSupervisorDialog}
        onClose={() => { setOpenSupervisorDialog(false); setOpenBranchesDialog(true); }}
      >
        <DialogTitle>{t('select_supervisor_title') || t('supervisor')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('select_supervisor_prompt') || `Select a supervisor for ${selectedCandidate?.fullname || ''}.`}
          </DialogContentText>
          <Box mt={2}>
            <FormControl fullWidth>
              <InputLabel>{t('supervisor')}</InputLabel>
              <Select
                value={selectedSupervisorId}
                onChange={(e) => setSelectedSupervisorId(e.target.value)}
                label={t('supervisor')}
              >
                <MenuItem value="">{t('none') || 'None'}</MenuItem>
                {supervisors.map((s) => (
                  <MenuItem key={s.id} value={s.id}>
                    {s.fullname} {s.email ? `(${s.email})` : ''}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setOpenSupervisorDialog(false); setOpenBranchesDialog(true); }} color="primary">
            {t('back') || 'Back'}
          </Button>
          <Button onClick={() => {
            setPendingSelectedModules([...selectedModules]);
            setOpenSupervisorDialog(false);
            setOpenModuleSelectionDialog(true);
          }} color="secondary">
            {t('next')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={openConfirmDialog}
        onClose={() => { setOpenConfirmDialog(false); setIsSupervisor(false); }}
      >
        <DialogTitle>{t('confirm_hire')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('confirm_hire_prompt')} {selectedCandidate?.fullname} ?
          </DialogContentText>
          <FormControlLabel
            sx={{ mt: 2 }}
            control={
              <Checkbox
                checked={isSupervisor}
                onChange={(e) => setIsSupervisor(e.target.checked)}
                color="secondary"
              />
            }
            label={
              <Box>
                <Typography variant="body2" fontWeight={600}>{t('mark_as_supervisor')}</Typography>
                <Typography variant="caption" color="text.secondary">{t('mark_as_supervisor_hint')}</Typography>
              </Box>
            }
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setOpenConfirmDialog(false); setIsSupervisor(false); }} color="primary">
            {t('cancel')}
          </Button>
          <Button onClick={handleConfirmUpgrade} color="secondary">
            {t('next')}
          </Button>
        </DialogActions>
      </Dialog>

      <Modal open={openModal} onClose={handleCloseModal}>
        <Box p={4} sx={{ backgroundColor: 'white', borderRadius: 2, width: 400, margin: 'auto', mt: 5 }}>
          <Typography variant="h6">{t('add_new_candidate')}</Typography>
          <TextField
            fullWidth
            label={t('email')}
            name="email"
            value={newCandidate.email}
            onChange={handleInputChange}
            sx={{ mt: 2 }}
          />
          <TextField
            fullWidth
            label={t('name')}
            name="fullname"
            value={newCandidate.fullname}
            onChange={handleInputChange}
            sx={{ mt: 2 }}
          />
          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel>{t('position')}</InputLabel>
            <Select
              name="position_id"
              value={newCandidate.position_id}
              onChange={handleInputChange}
            >
              {positions.map((position) => (
                <MenuItem key={position.id} value={position.id}>
                  {position.display_text}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel>{t('vacancy_select_optional')}</InputLabel>
            <Select
              multiple
              value={newCandidateVacancies}
              onChange={(e) => setNewCandidateVacancies(e.target.value)}
              renderValue={(selected) =>
                selected.map(id => allVacancies.find(v => v._id === id)?.title || id).join(', ')
              }
              label={t('vacancy_select_optional')}
            >
              {allVacancies.filter(v => v.status === 'open').map((vacancy) => (
                <MenuItem key={vacancy._id} value={vacancy._id}>
                  <Checkbox checked={newCandidateVacancies.includes(vacancy._id)} />
                  <ListItemText primary={vacancy.title} secondary={vacancy.position} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {/* Start date field removed as per requirements */}
          <Box mt={2} display="flex" justifyContent="flex-end">
            <Button onClick={handleCloseModal} color="primary">
              {t('cancel')}
            </Button>
            <LoadingButton
              onClick={handleAddCandidate}
              color="secondary"
              variant="contained"
              loading={loading}
              loadingLabel={t('sending_invitation')}
              sx={{ ml: 2 }}
            >
              {t('add')}
            </LoadingButton>
          </Box>
        </Box>
      </Modal>

      {loading && (
        <Modal open={loading}>
          <Box
            p={4}
            sx={{
              backgroundColor: 'white',
              borderRadius: 2,
              width: 300,
              margin: 'auto',
              mt: 10,
              textAlign: 'center',
            }}
          >
            <CircularProgress />
            <Typography variant="body1" mt={2}>
              {loadingMessage}
            </Typography>
          </Box>
        </Modal>
      )}

      {/* Edit Vacancies Dialog */}
      <Dialog open={openEditVacanciesDialog} onClose={() => setOpenEditVacanciesDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('candidate_edit_vacancies')} — {selectedCandidate?.fullname}</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            {t('vacancy_select_optional')}
          </DialogContentText>
          <List dense>
            {allVacancies.filter(v => v.status === 'open').map((vacancy) => (
              <ListItemButton
                key={vacancy._id}
                onClick={() => {
                  setEditVacanciesSelected(prev =>
                    prev.includes(vacancy._id)
                      ? prev.filter(id => id !== vacancy._id)
                      : [...prev, vacancy._id]
                  );
                }}
              >
                <Checkbox checked={editVacanciesSelected.includes(vacancy._id)} />
                <ListItemText primary={vacancy.title} secondary={vacancy.position} />
              </ListItemButton>
            ))}
            {allVacancies.filter(v => v.status === 'open').length === 0 && (
              <ListItem>
                <ListItemText primary={t('vacancy_no_vacancies')} />
              </ListItem>
            )}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenEditVacanciesDialog(false)}>{t('cancel')}</Button>
          <LoadingButton
            onClick={async () => {
              if (!selectedCandidate) return;
              setSavingVacancies(true);
              try {
                const res = await fetch(`${API_ENDPOINTS.BASEURL}/api/candidates/${selectedCandidate.email}/vacancies`, {
                  method: 'PUT',
                  headers: { 'Content-Type': 'application/json' },
                  credentials: 'include',
                  body: JSON.stringify({ vacancy_ids: editVacanciesSelected }),
                });
                if (res.ok) {
                  setSnackbar({ open: true, message: t('candidate_vacancies_updated'), severity: 'success' });
                  // Update local state
                  const updatedVacancies = editVacanciesSelected.map(vid => {
                    const v = allVacancies.find(vac => vac._id === vid);
                    return { _id: vid, title: v?.title || 'Unknown' };
                  });
                  setCandidates(prev => prev.map(c =>
                    c.email === selectedCandidate.email ? { ...c, vacancy_ids: updatedVacancies } : c
                  ));
                  setFilteredCandidates(prev => prev.map(c =>
                    c.email === selectedCandidate.email ? { ...c, vacancy_ids: updatedVacancies } : c
                  ));
                  setSelectedCandidate(prev => prev ? { ...prev, vacancy_ids: updatedVacancies } : prev);
                  setOpenEditVacanciesDialog(false);
                } else {
                  setSnackbar({ open: true, message: t('candidate_vacancies_error'), severity: 'error' });
                }
              } catch {
                setSnackbar({ open: true, message: t('candidate_vacancies_error'), severity: 'error' });
              }
              setSavingVacancies(false);
            }}
            variant="contained"
            loading={savingVacancies}
            loadingLabel={t('please_wait')}
          >
            {t('save')}
          </LoadingButton>
        </DialogActions>
      </Dialog>

      <FixedSnackbar
      open={snackbar.open}
      autoHideDuration={15000}
      onClose={handleSnackbarClose}
      message={snackbar.message}
      severity={snackbar.severity}
      />

      <Dialog
        open={openRequestDocsDialog}
        onClose={() => setOpenRequestDocsDialog(false)}
      >
        <DialogTitle>{t('confirm_docs_request')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('confirm_docs_prompt')}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenRequestDocsDialog(false)} color="primary" disabled={loadingRequestDocs}>
            {t('cancel')}
          </Button>
          <LoadingButton
            onClick={async () => {
              setLoadingRequestDocs(true); // Start spinner
              try {
                const response = await fetch(
                  `${API_ENDPOINTS.BASEURL}/api/candidates/${selectedCandidate.email}/request-documents`,
                  {
                    method: 'POST',
                    credentials: 'include',
                  }
                );
                const data = await response.json();
                if (response.ok) {
                  setSnackbar({ open: true, message: t('request_docs_sent'), severity: 'success' });
                  setCandidates((prev) =>
                    prev.map((c) =>
                      c.email === selectedCandidate.email ? { ...c, docs_requested: true } : c
                    )
                  );
                  setFilteredCandidates((prev) =>
                    prev.map((c) =>
                      c.email === selectedCandidate.email ? { ...c, docs_requested: true } : c
                    )
                  );
                  setSelectedCandidate((prev) =>
                    prev ? { ...prev, docs_requested: true } : prev
                  );
                } else {
                  setSnackbar({ open: true, message: data.message, severity: 'error' });
                }
              } catch (err) {
                setSnackbar({ open: true, message: t('request_docs_error'), severity: 'error' });
              } finally {
                setLoadingRequestDocs(false); // Stop spinner
                setOpenRequestDocsDialog(false);
              }
            }}
            color="secondary"
            variant="contained"
            loading={loadingRequestDocs}
            loadingLabel={t('please_wait')}
          >
            {t('confirm')}
          </LoadingButton>
        </DialogActions>
      </Dialog>

      {/* Add the modal at the end of the component */}
      <AdminUserDetailsModal
        open={detailsModalOpen}
        onClose={() => setDetailsModalOpen(false)}
        userEmail={selectedCandidate?.email}
        fullname={selectedCandidate?.fullname}
        position={selectedCandidate?.position}
        onDeleted={handleUserDeleted}
      />
      
    </Box>
  );
}

export default Candidates;